import { SpinResponse } from '@cgs/data-access';
import { Action, Container, FunctionAction } from '@cgs/wge';
import {
  GAME_STATE_MACHINE_PROVIDER_TOKEN,
  IGameStateMachineProvider,
} from '../../reels_engine/game_components_providers/i_game_state_machine_provider';
import { GameStateMachine } from '../../reels_engine/state_machine/game-state-machine';
import { T_IFadeReelsProvider, T_ISlotPrimaryActionsProvider } from '../../type_definitions';
import { ISlotPrimaryActionsProvider } from './interfaces/i_slot_primary_actions_provider';
import { ISlotPrimaryAnimationProvider } from './interfaces/i_slot_primary_animation_provider';
import {
  WIN_LINES_CYCLING_CONDITION,
  WinLinesCyclingCondition,
} from './interfaces/win-lines-cycling-condition';
import { IFadeReelsProvider } from './win_lines/i_fade_reels_provider';

export class SlotPrimaryAnimationProvider implements ISlotPrimaryAnimationProvider {
  private readonly container: Container;
  private readonly _gameStateMachine: GameStateMachine<SpinResponse>;
  private _primarySlotActionsProvider: ISlotPrimaryActionsProvider;

  constructor(container: Container) {
    this.container = container;
    console.log('load ' + this.constructor.name);
    this._gameStateMachine = this.container.resolve<IGameStateMachineProvider>(
      GAME_STATE_MACHINE_PROVIDER_TOKEN
    ).gameStateMachine;
  }

  clearWinLines(): void {
    this._gameStateMachine.regularSpins.animation.end();
    this.container.resolve<IFadeReelsProvider>(T_IFadeReelsProvider).enableFade(false);
  }

  get gameStateMachine(): GameStateMachine<SpinResponse> {
    return this._gameStateMachine;
  }

  get primarySlotActionsProvider(): ISlotPrimaryActionsProvider {
    if (!this._primarySlotActionsProvider) {
      this._primarySlotActionsProvider = this.container.resolve<ISlotPrimaryActionsProvider>(
        T_ISlotPrimaryActionsProvider
      );
    }
    return this._primarySlotActionsProvider;
  }

  attachPrimaryAnimationsToStateMachine(): void {
    this._gameStateMachine.accelerate.setLazyAnimation(() => this.buildStartSlotAction());
    this._gameStateMachine.stopping.setLazyAnimation(() => this.buildStopSlotAction());
    this._gameStateMachine.immediatelyStop.setLazyAnimation(() =>
      this.buildImmediatelyStopSlotAction()
    );

    this._gameStateMachine.regularSpins.setLazyAnimation(() => this.buildWinLinesAction());

    this._gameStateMachine.shortWinLines.setLazyAnimation(() => this.buildShortWinLinesAction());
    this._gameStateMachine.endFreeSpins.setLazyAnimation(() => this.buildShortWinLinesAction());

    this._gameStateMachine.respin.setLazyAnimation(() => this.buildRespinWinLinesAction());

    this._gameStateMachine.beginFreeSpins.setLazyAnimation(() => this.buildSpecialWinLinesAction());
    this._gameStateMachine.beginBonus.setLazyAnimation(() => this.buildSpecialWinLinesAction());
    this._gameStateMachine.beginScatter.setLazyAnimation(() => this.buildSpecialWinLinesAction());
  }

  buildStartSlotAction(): Action {
    this._gameStateMachine.regularSpins.setLazyAnimation(() => this.buildWinLinesAction());
    return this.primarySlotActionsProvider.getStartSlotAction();
  }

  buildStopSlotAction(): Action {
    return this.primarySlotActionsProvider.getStopSlotAction();
  }

  buildImmediatelyStopSlotAction(): Action {
    return this.primarySlotActionsProvider.getImmediatelyStopSlotAction();
  }

  buildWinLinesAction(): Action {
    const cyclingCondition = this.container.resolve<WinLinesCyclingCondition>(
      WIN_LINES_CYCLING_CONDITION,
      { optional: true }
    );

    // One pass of win lines has already played in the shortWinLines state;
    // when cycling is suppressed (e.g. externally driven autoplay), starting
    // the loop here would only get cut off by the next spin. The short pass
    // leaves the reels fade enabled expecting the loop to take over, so with
    // the loop skipped it has to be turned off here.
    if (cyclingCondition && !cyclingCondition.isCyclingAllowed) {
      return new FunctionAction(() =>
        this.container.resolve<IFadeReelsProvider>(T_IFadeReelsProvider).enableFade(false)
      );
    }

    return this.primarySlotActionsProvider.getWinLinesAction();
  }

  buildShortWinLinesAction(): Action {
    return this.primarySlotActionsProvider.getShortWinLinesAction();
  }

  buildRespinWinLinesAction(): Action {
    return this.primarySlotActionsProvider.getRespinWinLinesAction();
  }

  buildSpecialWinLinesAction(): Action {
    return this.primarySlotActionsProvider.getSpecialWinLinesAction();
  }
}
